"use client";

/**
 * Module-level cache for form schema fetches.
 *
 * Public landing pages can have several form CTAs ("Book flights", "RSVP", etc.).
 * Without caching, each FormModal opens with a network round-trip to load the form
 * schema, which can take seconds. SmartButton warms this cache as soon as it renders
 * a form-link CTA, so by the time the user clicks the schema is already in memory.
 *
 * The schema is loaded via the `/api/forms/renderable` server route (service-client
 * read + app-level access gate) rather than a direct browser query, because the
 * hardened RLS on `nowadays.forms` hides invited-only rows from the authenticated
 * client and would otherwise blank the form for invited viewers.
 */

import type { ViewerForm, SubmittedFormResponse } from "@/features/forms/_logic/viewer-form";
import { resolveClosedMessage } from "@/features/forms/_logic/form-status";
import { FORM_PREFETCH_IDLE_TIMEOUT_MS } from "./constants";
import { runOnIdle } from "./utils/run-on-idle";

/**
 * Outcome of loading a form's renderable schema.
 * - `ok`: the caller may view the form; `form` holds the browser-safe projection,
 *   `submittedResponse` is populated only for a single-submission form the
 *   current viewer already has a submitted response for (bundled here rather
 *   than requiring a second round-trip to fetch the recap separately).
 * - `locked`: a non-public form the caller isn't invited to; `name` for display,
 *   `redirectTo` set when the visitor should be sent to enforced SSO sign-in.
 * - `closed`: the form is no longer accepting responses; `closedMessage` is the
 *   resolved visitor-facing copy (the admin's message, or the default).
 * - `not_found`: no such form.
 */
export type FormLoadResult =
  | { status: "ok"; form: ViewerForm; submittedResponse: SubmittedFormResponse | null }
  | { status: "locked"; name: string | null; redirectTo: string | null }
  | { status: "closed"; name: string | null; closedMessage: string }
  | { status: "not_found" };

const formCache = new Map<string, Promise<FormLoadResult>>();

/**
 * Cache key for a form load. Includes the landing page id because the access
 * decision can differ by page (a page's `sso_domain` may grant access the form's
 * own settings wouldn't), so the same form must not reuse a decision cached under
 * a different page during client-side navigation.
 */
function cacheKey(formId: number, landingPageId?: number | null): string {
  return `${formId}:${landingPageId ?? ""}`;
}

async function fetchRenderableForm(formId: number, landingPageId?: number | null): Promise<FormLoadResult> {
  const pageParam = landingPageId ? `&landing_page_id=${landingPageId}` : "";
  const res = await fetch(`/api/forms/renderable?form_id=${formId}${pageParam}`);
  if (res.ok) {
    const body = (await res.json()) as
      | { form: ViewerForm; submittedResponse: SubmittedFormResponse | null }
      | { closed: { name: string | null; closedMessage: string | null } };
    if ("closed" in body) {
      return {
        status: "closed",
        name: body.closed.name ?? null,
        closedMessage: resolveClosedMessage(body.closed.closedMessage),
      };
    }
    return { status: "ok", form: body.form, submittedResponse: body.submittedResponse };
  }
  if (res.status === 404) {
    return { status: "not_found" };
  }
  const body = (await res.json().catch(() => ({}))) as {
    name?: string | null;
    redirectTo?: string | null;
  };
  // 401/403 — non-public form the caller isn't authorized to view.
  return { status: "locked", name: body.name ?? null, redirectTo: body.redirectTo ?? null };
}

/**
 * Returns the cached promise for a form, or starts a new fetch and caches it.
 *
 * @param formId - The form to load.
 * @param landingPageId - The landing page the form is opened from (for inherited
 *   SSO access). Omit for the standalone form context.
 * @returns A promise resolving to the load outcome (ok / locked / closed / not_found).
 */
export function getFormCached(formId: number, landingPageId?: number | null): Promise<FormLoadResult> {
  // The module-scope cache now carries a per-viewer submittedResponse (PII), not just
  // viewer-independent schema — never share it across requests in a server process.
  if (typeof window === "undefined") return fetchRenderableForm(formId, landingPageId);

  const key = cacheKey(formId, landingPageId);
  const existing = formCache.get(key);
  if (existing) return existing;

  const promise = fetchRenderableForm(formId, landingPageId).catch((err) => {
    // Don't poison the cache on transient/network failure — let the next call retry.
    formCache.delete(key);
    throw err;
  });
  formCache.set(key, promise);
  return promise;
}

/**
 * Drops the cached load result for a form so the next {@link getFormCached} call
 * re-fetches it. Must be called after a successful submit/edit — otherwise a
 * reopened modal would keep reading the stale pre-submission `submittedResponse`
 * value for the rest of the page's lifetime, reproducing the exact bug this
 * field was added to fix. Also called when a submit finds the form closed, so a
 * reopened modal loads the closed state instead of the stale open form.
 *
 * @param formId - The form that was just submitted/edited.
 * @param landingPageId - The landing page the form was opened from (matches the
 *   key {@link getFormCached} uses).
 */
export function invalidateFormCache(formId: number, landingPageId?: number | null): void {
  formCache.delete(cacheKey(formId, landingPageId));
}

/**
 * Idle-deferred prefetch. Safe to call multiple times; only the first call hits the network.
 * No-op on the server.
 *
 * @param formId - The form to prefetch.
 * @param landingPageId - The landing page the form is opened from (matches the
 *   key {@link getFormCached} uses so the warmed entry is reused).
 */
export function prefetchForm(formId: number, landingPageId?: number | null): void {
  if (typeof window === "undefined") return;
  if (formCache.has(cacheKey(formId, landingPageId))) return;

  runOnIdle(() => {
    getFormCached(formId, landingPageId).catch((err) => {
      console.error("prefetchForm: error", err);
    });
  }, FORM_PREFETCH_IDLE_TIMEOUT_MS);
}
